import React from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import apiProducts from 'api/products';
import FileInput from 'components/layout/forms/FileInput';
import IFile from 'interfaces/forms/IFile';
import ReadOnlyTextInput from 'components/layout/forms/ReadOnlyTextInput';
import SearchableSelectField from 'components/layout/forms/SearchableSelectField';
import {
  getChoiceList,
  PRODUCT_FILE_TYPE,
  toOptions,
  useOfferChoices,
} from 'services/offers/offerChoices';
import translations from 'services/translations/translations';

import 'style/products/src/product/ProductFiles.css';
import TrashIcon from 'components/layout/icons/TrashIcon';
import downloadFile from 'services/files/downloadFile';
import { PRODUCT_IMAGE } from './ProductImage';

const ProductFiles = ({
  files,
  id,
  removeFile,
  setFiles,
}: {
  files: IFile[];
  id: string;
  removeFile: (file: IFile) => void;
  setFiles: any;
}): JSX.Element => {
  const { i18n, t } = useTranslation([
    translations.common,
    translations.products,
  ]);

  // The types of the choice list the admins manage; a file keeps a hidden type it has
  const choices = useOfferChoices();
  const getTypes = (current?: null | string) =>
    getChoiceList(choices, PRODUCT_FILE_TYPE, current, i18n.language);

  const addFile = (newFile: IFile) => {
    const newFileWithType = {
      ...newFile,
      type: getTypes(null)[0]?.value ?? null,
    };
    setFiles((previousFiles: IFile[]) => [...previousFiles, newFileWithType]);
  };

  const setFileType = (file: IFile, type: string) => {
    setFiles(
      files.map((existingFile: IFile) =>
        existingFile.key === file.key
          ? { ...existingFile, type: type === '' ? null : type }
          : existingFile
      )
    );
  };

  const openFile = async (file: IFile) => {
    if (file.id === null) {
      return;
    }

    const response = await apiProducts.files.get(file.id);
    downloadFile(response.data, response.headers);
  };

  const getFile = (file: IFile) => (
    <Row key={`product-file-${file.key}`} className="product-file">
      <Col>
        <SearchableSelectField
          id={`product-file-${file.key}`}
          label={t('Type')}
          name="product-file-select"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFileType(file, event.target.value)
          }
          options={toOptions(getTypes(file.type), false)}
          value={file.type === null ? '' : file.type}
        />
      </Col>
      <Col>
        <ReadOnlyTextInput label={t('Name')} verticalAlignMatchInput>
          <p
            className="product-file-link"
            onClick={() => openFile(file)}
            tabIndex={-1}
          >
            {file.name}
          </p>
          <TrashIcon onClick={() => removeFile(file)} />
        </ReadOnlyTextInput>
      </Col>
    </Row>
  );

  return (
    <div className="product-files">
      <h3>{t('Files')}</h3>
      <div className="product-files-list">
        {/* The product image has a place of its own */}
        {files
          .filter((file: IFile) => file.type !== PRODUCT_IMAGE)
          .map((file: IFile) => getFile(file))}
      </div>
      <FileInput addFile={addFile} files={files} id={id} />
    </div>
  );
};

export default ProductFiles;
